import React, {useContext, useState, useEffect, useCallback, useRef} from "react";
import PropTypes from "prop-types";
import {Link} from "react-router-dom";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileExport} from "@fortawesome/free-solid-svg-icons";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {useSocket} from "../../contexts/socket.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";
import useApi from "../../hooks/useApi.js";

// Utils
import {prettyDateInUserTimezone} from "../../utils/helpers.js";

// Components
import FacilityName from "./TableFacilitiesFacilityName.js";
import FacilitySettings from "./TableFacilitiesFacilitySettings.js";
import BaseTable from "../../components/BaseTable.js";
import Pagination from "../../components/Pagination.js";
import Dropdown from "../../components/Dropdown.js";
import ModalExport from "../../components/ModalExport.js";
import ModalViewAllFacilities from "../general/ModalViewAllFacilities.js";

// Style
import {voice} from "../../style/components/typography.js";
import {pad} from "../../style/components/variables.js";
import {
  Heading,
  Inline,
  Text,
  TableWrapper,
  TableHeaderWrap,
  TableFooter,
  Button,
  ListItem,
  List,
  TableMenu,
  Warning,
  Error,
  Small
} from "../../style/components/general.js";

const TableFacilities = ({
  facilities,
  setFacilities,
  query,
  filters,
  orderBy = "asc",
  showArchived = false,
  currentPage,
  setCurrentPage,
  setTarget,
  setShowModalSettings,
  setShowModalFavorite,
  lock,
  retrievedLock
}) => {
  const isMounted = useMountedState();

  const {roleCanAccessResource} = useContext(AuthContext);
  const socket = useSocket();

  const [visible, setVisible] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModalExport, setShowModalExport] = useState(false);
  const [showModalViewAll, setShowModalViewAll] = useState(false);
  // Pagination
  const [total, setTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(1);
  const [limits, setLimits] = useState([]);
  const [limit, setLimit] = useState(5);
  const [trueLimit, setTrueLimit] = useState(5);

  const retrievingFacilities = useRef(false);

  const {api} = useApi("facilities");

  const formatFacilities = useCallback(
    prevFacilities =>
      prevFacilities?.map(facility => {
        const {isDeleted, id, slug, type, region, details, checksheets, events} = facility;
        return {
          isDeleted,
          id: id,
          name: (
            <FacilityName
              facility={facility}
              socket={socket}
              setTarget={setTarget}
              setShowModalFavorite={setShowModalFavorite}
              lock={lock}
            />
          ),
          type: <List column>{type}</List>,
          region: region?.name ? <Text>{region.name.toUpperCase()}</Text> : null,
          jobs: details?.jobs?.length > 0 && (
            <List column>
              {details.jobs.map(job => (
                <ListItem key={job}>{job}</ListItem>
              ))}
            </List>
          ),
          checksheets:
            roleCanAccessResource("facility_checksheet") &&
            checksheets
              ?.filter(
                ({frequency}) =>
                  !filters?.frequencies ||
                  (frequency?.name && filters.frequencies.includes(frequency.name))
              )
              ?.map(({id: cId, name: cName, frequency, dateDue, draftCount, overdueCount}) => (
                <NotifyList key={cId} column>
                  <TableLink to={`/facilities/${slug}/tasks`} title={cName}>
                    {cName}
                    {frequency?.name && <Frequency>({frequency.name})</Frequency>}
                  </TableLink>
                  {(!filters?.checksheets || filters.checksheets?.includes("DUE")) && dateDue && (
                    <NotifyListItem>
                      <Small>Due by {prettyDateInUserTimezone(dateDue)}</Small>
                    </NotifyListItem>
                  )}
                  {(!filters?.checksheets || filters.checksheets?.includes("INCOMPLETE")) &&
                    draftCount > 0 && (
                      <NotifyListItem>
                        <Warning>{draftCount} incomplete record(s)</Warning>
                      </NotifyListItem>
                    )}
                  {(!filters?.checksheets || filters.checksheets?.includes("OVERDUE")) &&
                    overdueCount > 0 && (
                      <NotifyListItem>
                        <Error>{overdueCount} overdue record(s)</Error>
                      </NotifyListItem>
                    )}
                </NotifyList>
              )),
          events:
            roleCanAccessResource("events") &&
            events?.map(({id: eId, name: eName, available, dateDue, draftCount, overdueCount}) => (
              <NotifyList key={eId} column>
                <TableLink to={`/facilities/${slug}/schedule`} title={eName}>
                  {eName}
                </TableLink>
                {available && (
                  <NotifyListItem>
                    <Small>Due by {prettyDateInUserTimezone(dateDue)}</Small>
                  </NotifyListItem>
                )}
                {draftCount > 0 && (
                  <NotifyListItem>
                    <Warning>{draftCount} incomplete record(s)</Warning>
                  </NotifyListItem>
                )}
                {overdueCount > 0 && (
                  <NotifyListItem>
                    <Error>{overdueCount} overdue record(s)</Error>
                  </NotifyListItem>
                )}
              </NotifyList>
            )),
          settings: (roleCanAccessResource("facility", "delete") ||
            roleCanAccessResource("facility", "archive") ||
            roleCanAccessResource("facility", "create")) && (
            <FacilitySettings
              facility={facility}
              setTarget={setTarget}
              setShowModalSettings={setShowModalSettings}
            />
          )
        };
      }),
    [
      roleCanAccessResource,
      setShowModalFavorite,
      setShowModalSettings,
      setTarget,
      socket,
      lock,
      filters
    ]
  );

  const getFacilities = useCallback(
    p => {
      setLoading(true);

      const filter = {};

      const payload = {
        page: p || 1,
        limit: trueLimit,
        orderBy: orderBy,
        groupBy: "name",
        withChecksheets: true,
        withEvents: true,
        withTasksDue: true,
        withDeleted: showArchived,
        summary: true
      };

      if (query) filter.Search = query;
      if (filters?.types) filter.Type = filters.types;
      if (filters?.regions) filter.Region = filters.regions;
      if (filters?.states) filter.State = filters.states;
      if (filters?.checksheets) filter.Checksheet = filters.checksheets;
      if (filters?.frequencies) filter.Frequencies = filters.frequencies;
      if (filters?.checksheetSearch) [filter.ChecksheetSearch] = filters.checksheetSearch;

      if (filter && Object.keys(filter).length > 0) payload.filter = JSON.stringify(filter);

      api.callGet(null, payload).then(({status, data}) => {
        if (status === 200 && data) {
          const {facilities: facilityData, page, pages, total: cTotal} = data;
          const formatted = formatFacilities(facilityData);

          // Page Limit
          const option = value => ({
            label: value,
            value
          });

          let count = 10;
          const temp = [option(5)];
          while (count < cTotal + 10 && count <= 30) {
            temp.push(option(count));
            count += 10;
          }

          setTotal(cTotal);
          setCurrentPage(page);
          setPageTotal(pages);
          setVisible(prev => (limit <= 30 || page === 1 ? formatted : [...prev, ...formatted]));
          setFacilities(facilityData);
          setLimits(temp);
          setLoading(false);
          retrievingFacilities.current = false;
        }
      });
    },
    [
      api,
      filters,
      formatFacilities,
      trueLimit,
      limit,
      orderBy,
      query,
      setCurrentPage,
      setFacilities,
      showArchived
    ]
  );

  // Initial Load
  useEffect(() => {
    if (isMounted() && facilities === null && !retrievingFacilities.current && retrievedLock) {
      retrievingFacilities.current = true;
      getFacilities();
    }
  }, [facilities, getFacilities, isMounted, retrievedLock]);

  // Load more on scroll
  const handleScroll = useCallback(() => {
    const {scrollHeight, clientHeight, scrollTop} = document.documentElement;
    const maxScrollTop = scrollHeight - clientHeight;

    if (
      limit > 30 &&
      scrollTop + 1 >= maxScrollTop &&
      currentPage < pageTotal &&
      !loading &&
      !retrievingFacilities?.current
    ) {
      retrievingFacilities.current = true;
      getFacilities(currentPage + 1);
    }
  }, [currentPage, limit, loading, pageTotal, getFacilities]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => document.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Update facilities on query, sort, limit or delete
  useEffect(() => {
    if (isMounted()) setFacilities(null);
  }, [isMounted, query, orderBy, limit, showArchived, setFacilities, filters]);

  return (
    <>
      <TableWrapper>
        <TableHeaderWrap>
          <TableHeading>Facilities</TableHeading>
          <TableMenu>
            {roleCanAccessResource("facility", "export") && facilities && facilities.length > 0 && (
              <Button type="button" title="Export Facilities" onClick={() => setShowModalExport(true)}>
                <FontAwesomeIcon icon={faFileExport} />
              </Button>
            )}
          </TableMenu>
        </TableHeaderWrap>
        <BaseTable
          headings={{
            settings: {header: " ", disabled: true},
            id: {header: "", disabled: true},
            name: {header: "Name", disabled: true},
            type: {header: "Type(s)", disabled: true},
            region: {header: "Region", disabled: true},
            jobs: {header: "Job Number(s)", disabled: true},
            checksheets: {
              header: roleCanAccessResource("facility_checksheet") ? "Checksheets" : "",
              disabled: true
            },
            events: {header: roleCanAccessResource("event") ? "Events" : "", disabled: true}
          }}
          data={visible}
          updateData={() => getFacilities(currentPage)}
          orderBy={orderBy}
          groupBy="name"
          loading={loading}
          alignRows="top"
          cellOverflow
          cellFullWidth
        />
        <TableFooter>
          {total > 0 && (
            <>
              <Inline>
                {limits.length > 0 && (
                  <PageLimit>
                    <Dropdown
                      options={limits}
                      selection={limit}
                      setSelection={selection => {
                        setLimit(selection);
                        setTrueLimit(selection <= 30 ? selection : 10);
                        setCurrentPage(1);
                      }}
                    />
                  </PageLimit>
                )}
                <Text quiet>
                  {limits.length > 0 && "per page, "}
                  <ModalViewLink
                    title="View All Facilities"
                    disabled={!total || total <= 10}
                    onClick={!total || total <= 10 ? undefined : () => setShowModalViewAll(true)}>
                    {total} total
                  </ModalViewLink>
                </Text>
              </Inline>
              {pageTotal > 1 && (
                <Pagination
                  current={currentPage}
                  setCurrent={setCurrentPage}
                  pageTotal={pageTotal}
                  updateData={() => getFacilities(currentPage)}
                  loading={loading}
                />
              )}
            </>
          )}
        </TableFooter>
      </TableWrapper>

      {showModalExport && (
        <ModalExport
          visible={showModalExport}
          setVisible={setShowModalExport}
          getExport={async exportParams =>
            api.callGet(null, {
              ...exportParams
            })
          }
        />
      )}

      {showModalViewAll && (
        <ModalViewAllFacilities visible={showModalViewAll} setVisible={setShowModalViewAll} />
      )}
    </>
  );
};

TableFacilities.propTypes = {
  facilities: PropTypes.arrayOf(PropTypes.any),
  setFacilities: PropTypes.func.isRequired,
  query: PropTypes.string,
  filters: PropTypes.objectOf(PropTypes.any),
  orderBy: PropTypes.string,
  showArchived: PropTypes.bool,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setTarget: PropTypes.func.isRequired,
  setShowModalSettings: PropTypes.func.isRequired,
  setShowModalFavorite: PropTypes.func.isRequired,
  lock: PropTypes.string,
  retrievedLock: PropTypes.bool
};

// Style Overrides
const TableHeading = styled(Heading)`
  white-space: nowrap;
`;

const PageLimit = styled.div`
  margin-right: ${pad}px;

  select {
    padding: ${pad / 2}px;
  }
`;

const NotifyList = styled(List)`
  list-style: circle;
`;

const NotifyListItem = styled(ListItem)`
  padding: ${pad / 2}px 0;
  margin-left: ${pad * 1.5}px;
`;

const TableLink = styled(Link)`
  font-weight: bold;
  align-self: flex-start;
  vertical-align: top;

  &:hover {
    color: ${({theme}) => theme.primary};
    transition: all ease 0.5s;
  }
`;

const Frequency = styled.span`
  text-transform: uppercase;
  padding: 0 ${pad / 2}px;
  color: ${({theme}) => theme.secondary};
  ${voice.quiet};
`;

const ModalViewLink = styled.span`
  ${({disabled}) =>
    !disabled &&
    css`
      text-decoration: underline;
      cursor: pointer;
    `}
`;

export default TableFacilities;
